import { Box, ButtonNew, ModBox, Spacer, Text } from '@jfs/ui-new'
import { hyphenSplashSeparatedDate } from '@jfs/utils'
import { useIsFocused, useNavigation } from '@react-navigation/native'
import { useEffect, useRef, useState } from 'react'
import { FlatList } from 'react-native'
import { ImageWithInfoSerializer } from '../../comps/ImageWithInfoSerializer/ImageWithInfoSerializer'
import { ShimmerView } from '../../comps/ShimmerView'
import { useLazyApiData, useStores } from '../../data/store'
import { Routes } from '../../nav/routes'
import { hyphenSeparatedDate, isMaxDateRangeSelected } from '../../util/billPaymentUtil'
import { getPrevDate, getSelectedDate, isOsMobile } from '../../util/util'
import { TransactionCard } from '../BillPayments/TransactionCard'
import { SearchBarWithFilters } from './SearchBarWithFilters'

import { RefreshControl as RefreshControlMobile } from 'react-native'
import { RefreshControl as RefreshControlMobileWeb } from 'react-native-web-refresh-control'
import { actionMenuStrings } from '../../data/static/strings'
import { INFINITE_SCROLL_WINDOW, INFINITE_SCROLL_WINDOW_INDEX } from '../../util/common'

const DEFAULT_OFFSET = -1

const BillPayTransactions = () => {
  const abortControllerRef = useRef<AbortController>()
  const [offset, setOffset] = useState(0)
  const [viewMore, setViewMore] = useState(false)
  const {
    orders,
    getOrderHistoryApi,
    updateTransactionOrder,
    filter,
    selectedDateInfo,
    resetFilters,
    loadMoreTransactions,
    updateDefaultDateRange,
    clearOrder,
  } = useStores((root) => ({
    getOrderHistoryApi: root.billPayments.getOrderHistoryApi,
    updateTransactionOrder: root.billPayments.updateTransactionOrder,
    filter: root.billPayments.filter,
    selectedDateInfo: root.billPayments.selectedDateInfo,
    resetFilters: root.billPayments.resetFilters,
    loadMoreTransactions: root.billPayments.loadMoreTransactions,
    orders: root.billPayments.orders,
    updateDefaultDateRange: root.billPayments.updateSelectedDateInfo,
    clearOrder: root.billPayments.clearOrder,
  }))

  const navigation = useNavigation()
  const isFocussed = useIsFocused()

  const [orderHistoryApi, { state: orderHistoryState, data: orderHistoryData }] = useLazyApiData(getOrderHistoryApi)
  const [fetchMoreTransactionApi, { state: fetchMoreTransactionState, data: fetchMoreTransactionData }] =
    useLazyApiData(getOrderHistoryApi)

  const renderItem = ({ item }) => {
    const onSelect = (txn: any) => {
      updateTransactionOrder({
        ...txn,
        shortName: txn.product.name,
        timeStamp: hyphenSeparatedDate(new Date(txn?.payment?.paymentAt), 'dd-mm-yyyy hh:mm:ss'),
      })
      // navigate to transaction detail screen
      navigation.navigate(Routes.BILL_PAYMENTS_TRANSACTIONS_DETAILS as never, { data: txn } as never)
    }

    return (
      <Box ph={24}>
        <TransactionCard item={item} onSelect={onSelect} />
      </Box>
    )
  }

  const getUpdateCurrentDateTime = () => {
    let dateObj: any = {
      fromDate: getSelectedDate(INFINITE_SCROLL_WINDOW_INDEX),
      toDate: new Date(),
      offset: 0,
    }
    return dateObj
  }

  useEffect(() => {
    if (orderHistoryState === 'fulfilled') {
      if (orderHistoryData?.length) {
        if (orderHistoryData?.length < 10) {
          if (filter?.selectedDateInfo?.fromDate && filter?.selectedDateInfo?.toDate) {
            setViewMore(false)
          } else {
            // update from and to date and offset
            reconcileDate()
          }
        } else {
          // more transaction are available
          setViewMore(true)
        }
      } else {
        setViewMore(false) // no transactions
      }
    } else if (orderHistoryState === 'rejected') {
      setViewMore(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderHistoryState])

  useEffect(() => {
    if (fetchMoreTransactionState === 'fulfilled') {
      if (fetchMoreTransactionData?.length) {
        if (fetchMoreTransactionData?.length < 10) {
          setViewMore(false)
          reconcileDate()
        } else {
          // more transaction are available
          setViewMore(true)
        }
      } else {
        reconcileDate()
      }
    } else if (fetchMoreTransactionState === 'rejected') {
      if (/no record present/i.test(fetchMoreTransactionData?.message)) {
        reconcileDate()
      } else {
        setViewMore(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchMoreTransactionState])

  useEffect(() => {
    if (isFocussed) {
      abortControllerRef?.current?.abort()
      abortControllerRef.current = new AbortController()
      const dateObj: any = getUpdateCurrentDateTime()
      const fromDate = hyphenSplashSeparatedDate(dateObj.fromDate, 'yyyy-MM-dd hh:mm:ss').replace(' ', 'T')
      const toDate = hyphenSplashSeparatedDate(dateObj.toDate, 'yyyy-MM-dd hh:mm:ss').replace(' ', 'T')
      resetFilters()
      updateDefaultDateRange(dateObj)
      orderHistoryApi(fromDate, toDate, abortControllerRef?.current, 10, offset)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocussed])

  const reconcileDate = () => {
    if (filter?.selectedDateInfo?.fromDate && filter?.selectedDateInfo?.toDate) {
      setViewMore(false)
    } else {
      // update from and to date and offset
      // check maximum date is reached or not
      const dateObject = {
        toDate: selectedDateInfo?.fromDate?.getTime(),
        fromDate: getPrevDate(selectedDateInfo?.fromDate, INFINITE_SCROLL_WINDOW),
        dateLabel: actionMenuStrings.infiniteScrollRange,
      }
      const maxDateRangeSelected = isMaxDateRangeSelected(dateObject)
      if (maxDateRangeSelected) {
        setViewMore(false)
      } else {
        updateDefaultDateRange(dateObject)
        setOffset(DEFAULT_OFFSET)
        setViewMore(true)
      }
    }
  }

  const onLoadMore = () => {
    setViewMore(false)
    let offsetVal = offset + 1 // increase the offset.
    let dateObject: any = selectedDateInfo
    if (filter?.selectedDateInfo?.fromDate && filter?.selectedDateInfo?.toDate) {
      dateObject = filter.selectedDateInfo
    }

    abortControllerRef?.current?.abort()
    abortControllerRef.current = new AbortController()
    setOffset(offsetVal)
    // loadmore action method instead order history api
    loadMoreTransactions(fetchMoreTransactionApi, dateObject, offsetVal, abortControllerRef?.current)
  }

  const handleApply = () => {
    clearOrder()
    setOffset(0)
    setViewMore(false)

    let dateObj: any = selectedDateInfo
    if (filter?.selectedDateInfo?.fromDate && filter?.selectedDateInfo?.toDate) {
      dateObj = filter?.selectedDateInfo
    }

    const fromDate = hyphenSplashSeparatedDate(dateObj.fromDate, 'yyyy-MM-dd hh:mm:ss').replace(' ', 'T')
    const toDate = hyphenSplashSeparatedDate(dateObj.toDate, 'yyyy-MM-dd hh:mm:ss').replace(' ', 'T')
    abortControllerRef?.current?.abort()
    abortControllerRef.current = new AbortController()
    orderHistoryApi(fromDate, toDate, abortControllerRef?.current, 10, 0)
  }

  const handleRetry = (resetDate = false) => {
    abortControllerRef?.current?.abort()
    abortControllerRef.current = new AbortController()
    let dateObj: any = getUpdateCurrentDateTime()
    let retryOffset = offset

    if (resetDate) {
      resetFilters()
      dateObj = {
        ...dateObj,
        dateLabel: 'infinite scroll',
      }
      retryOffset = 0
      updateDefaultDateRange(dateObj)
      setOffset(retryOffset)
    } else if (filter?.selectedDateInfo?.fromDate && filter?.selectedDateInfo?.toDate) {
      dateObj = filter?.selectedDateInfo
    }

    const fromDate = hyphenSplashSeparatedDate(dateObj.fromDate, 'yyyy-MM-dd hh:mm:ss').replace(' ', 'T')
    const toDate = hyphenSplashSeparatedDate(dateObj.toDate, 'yyyy-MM-dd hh:mm:ss').replace(' ', 'T')

    orderHistoryApi(fromDate, toDate, abortControllerRef?.current, 10, retryOffset)
  }

  const handleFirstMonthTransactionsFailed = () => {
    if (/no record present/i.test(orderHistoryData?.message)) {
      // view older transactions
      const dateObject = {
        toDate: selectedDateInfo?.fromDate?.getTime(),
        fromDate: getPrevDate(selectedDateInfo?.fromDate, INFINITE_SCROLL_WINDOW),
        dateLabel: actionMenuStrings.infiniteScrollRange,
      }
      const maxDateRangeSelected = isMaxDateRangeSelected(dateObject)
      if (maxDateRangeSelected) {
        setViewMore(false)
      } else {
        updateDefaultDateRange(dateObject)
        setOffset(DEFAULT_OFFSET)
        setViewMore(false)
        const fromDate = hyphenSplashSeparatedDate(new Date(dateObject.fromDate), 'yyyy-MM-dd hh:mm:ss').replace(
          ' ',
          'T',
        )
        const toDate = hyphenSplashSeparatedDate(new Date(dateObject.toDate), 'yyyy-MM-dd hh:mm:ss').replace(' ', 'T')

        orderHistoryApi(fromDate, toDate, abortControllerRef?.current, 10, 0)
      }
    } else {
      handleRetry(true)
    }
  }

  const renderListFooterComponent = () => {
    if (viewMore) {
      return (
        <Box pv={24} f={1} align={'center'} alignSelf={'center'}>
          <ButtonNew
            loadingColor={'PRIMARY'}
            isLoading={orderHistoryState === 'pending' || fetchMoreTransactionState === 'pending'}
            title={'Show more'}
            onPress={onLoadMore}
            type={'secondary'}
            titleColor={'PRIMARY'}
            paddingHorizontal={24}
            borderColor={'PRIMARY'}
            isRaceTrack
          />
        </Box>
      )
    }
    if (orderHistoryState === 'pending' || fetchMoreTransactionState === 'pending') {
      return <ShimmerView type={Routes.TRANSACTIONS} itemCount={orderHistoryState === 'pending' ? 10 : 1} />
    }

    let dateObject: any = selectedDateInfo
    if (filter?.selectedDateInfo?.fromDate && filter?.selectedDateInfo?.toDate) {
      dateObject = filter.selectedDateInfo
    }
    const maxDateRangeSelected = isMaxDateRangeSelected(dateObject)

    if (
      !maxDateRangeSelected &&
      !viewMore &&
      fetchMoreTransactionState === 'rejected' &&
      orderHistoryState !== 'rejected' &&
      !/no record present/i.test(fetchMoreTransactionData?.message)
    ) {
      return (
        <Box f={1} p={32} justify={'center'} align={'center'}>
          <Text
            title={'Something went wrong'}
            fontFamily={'JioType-Bold'}
            fontSize={14}
            lineHeight={20}
            letterSpacing={-0.08}
          />
          <Spacer direction="vertical" size={8} />
          <Text
            title={'Try Again'}
            fontFamily={'JioType-Medium'}
            fontSize={14}
            lineHeight={20}
            letterSpacing={-0.08}
            fontWeight={'600'}
            color={'PRIMARY'}
            onPress={handleRetry}
            testID="retry"
          />
        </Box>
      )
    }
    return <Box height={20} />
  }

  const RefreshControl = isOsMobile() ? RefreshControlMobile : RefreshControlMobileWeb

  return (
    <Box bg={'SECONDARY'} f={1} pt={64}>
      <Box f={1} bg={'WHITE'} borderTopLeftRadius={24} borderTopRightRadius={24} zIndex={10}>
        <ModBox>
          <SearchBarWithFilters onApply={handleApply} />
          <Spacer size={8} direction={'vertical'} />
        </ModBox>
        <ModBox
          f={1}
          justify={'center'}
          show={orderHistoryState === 'rejected' && orders?.length === 0 && offset === 0}>
          <ImageWithInfoSerializer
            show={orderHistoryState === 'rejected'}
            screenName={
              orderHistoryState === 'rejected' && /no record present/i.test(orderHistoryData?.message)
                ? Routes.BILL_PAYMENTS_TRANSACTIONS
                : 'errorTxn'
            }
            actionButtonVisible={filter?.selectedDateInfo?.fromDate && filter?.selectedDateInfo?.toDate ? false : true}
            actionButtonName={
              /no record present/i.test(orderHistoryData?.message) &&
              !filter.selectedDateInfo.fromDate &&
              !filter.selectedDateInfo.toDate
                ? 'View older transactions'
                : 'Try Again'
            }
            onPress={handleFirstMonthTransactionsFailed}
          />
        </ModBox>
        <ModBox f={1} show={orders?.length > 0}>
          <FlatList
            data={orders}
            refreshControl={<RefreshControl refreshing={false} onRefresh={handleRetry} />}
            extraData={orders}
            renderItem={renderItem}
            scrollEnabled={true}
            showsVerticalScrollIndicator={true}
            ListFooterComponent={renderListFooterComponent}
            // TODO: if less transaction then the lazy load is flickering
            // onEndReached={({ distanceFromEnd }: any) => {
            //   if (distanceFromEnd === 0) {
            //     if (viewMore) {
            //       console.log('load more transactions', viewMore)
            //       onLoadMore()
            //     } else {
            //       console.log('no more transactions', fetchMoreTransactionData)
            //     }
            //   }
            // }}
            contentContainerStyle={{ flexGrow: 1 }}
          />
        </ModBox>
        {orderHistoryState === 'pending' && <ShimmerView type={Routes.TRANSACTIONS} itemCount={10} />}
      </Box>
    </Box>
  )
}

export default BillPayTransactions
